/**
 * Pathway Data Transformation Utilities
 *
 * Converts pathway data between different formats:
 * - Database format → Layout input format
 * - Layout output format → D3 visualization format
 */

/**
 * Transform pathways from database format to layout input format
 *
 * Database format:
 * {
 *   id: 'uuid',
 *   text: 'Node title',
 *   parentId: 'parent-uuid',
 *   type: 'ai-generated' | 'human' | 'start' | 'target',
 *   participantId: 'participant-uuid',
 *   category: 'technology',
 *   mood: 'good',
 *   reactions: 5,
 *   x: 100, // saved position
 *   y: 200,
 *   ...
 * }
 *
 * Layout format:
 * {
 *   id: 'uuid',
 *   title: 'Node title',
 *   parentIds: ['parent-uuid'],
 *   children: [], // populated by buildTree
 *   type: 'ai-generated' | 'human' | 'start' | 'target',
 *   participantId: 'participant-uuid',
 *   category: 'technology',
 *   mood: 'good',
 *   reactions: 5,
 *   ...metadata
 * }
 */
export function pathwaysToLayoutFormat(pathways) {
  console.log('🔄 pathwaysToLayoutFormat: Converting', pathways.length, 'pathways');
  console.log('Sample input pathway:', pathways[0]);

  // First pass: Create all nodes
  let nodes = pathways.map(pathway => {
    // Handle both single parentId and array parentIds for multi-parent support
    // Also handle snake_case parent_id from database (socket events)
    let parentIds = [];
    if (pathway.parentIds && Array.isArray(pathway.parentIds)) {
      parentIds = pathway.parentIds;
    } else if (pathway.parentId || pathway.parent_id) {
      parentIds = [pathway.parentId || pathway.parent_id];
    }

    return {
      id: pathway.id,
      title: pathway.text || 'Untitled',
      children: [], // Will be populated in second pass
      parentIds: parentIds, // Store for multi-parent arrows
      type: pathway.type || 'human',
      isHuman: pathway.type === 'human',
      participantId: pathway.participantId,
      participantName: pathway.participantName,
      category: pathway.category || 'neutral',
      mood: pathway.mood || 'neutral',
      categories: pathway.categories || (pathway.category ? [pathway.category] : []),
      votesUp: pathway.votesUp || 0,
      votesDown: pathway.votesDown || 0,
      reactions: pathway.reactions || 0,
      depth: pathway.depth || 0,

      // Shared nodes architecture (Dec 2025)
      created_by: pathway.created_by,
      is_scenario_starter: pathway.is_scenario_starter,
      contributors: pathway.contributors,

      // User-provided description (longer text/context)
      description: pathway.description,

      // Effect type of the edge from the primary parent to this node (v2).
      // Layout nodes are built by explicit mapping, so anything not named here
      // is invisible downstream even though metadata.originalData still has it.
      effectType: pathway.effectType,
      // Valence layer (§4b). null = unjudged, and the renderer draws no rail
      // for it; 'unknown' is a judgement and does draw one.
      valence: pathway.valence ?? null,
      valenceSource: pathway.valenceSource ?? pathway.valence_source ?? null,

      // AI-enhanced content separation (Dec 2025)
      human_text: pathway.human_text,
      ai_analysis: pathway.ai_analysis,
      ai_sources: pathway.ai_sources,
      node_analysis: pathway.node_analysis,

      // Omat-mode bridge metadata: this node has hidden intermediate ancestors
      // and is intentionally rendered with a dotted edge from visible parent.
      hasHiddenAncestors: !!pathway.hasHiddenAncestors,
      hiddenParentId: pathway.hiddenParentId || null,

      // Preserve any saved positions
      savedX: pathway.x,
      savedY: pathway.y,
      dragX: pathway.dragX,
      dragY: pathway.dragY,

      // Preserve merge/group data
      members: pathway.members, // Array of merged node IDs (for grouped nodes)
      mergedInto: pathway.mergedInto, // ID of group node (for merged-away nodes)

      // Metadata
      metadata: {
        originalData: pathway
      }
    };
  });

  // Second pass: Build children arrays from parentIds relationships
  const nodeMap = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  nodes.forEach(node => {
    if (node.parentIds && node.parentIds.length > 0) {
      // For tree structure: Only add to FIRST parent's children array
      // This ensures each node appears exactly once in the tree traversal
      // All parents are still kept in parentIds for arrow rendering
      const primaryParentId = node.parentIds[0];
      if (nodeMap[primaryParentId]) {
        if (!nodeMap[primaryParentId].children.includes(node.id)) {
          nodeMap[primaryParentId].children.push(node.id);
        }
      }
    }
  });

  console.log('Sample output node:', nodes[0]);
  // A node whose primary parent is not in the set has nothing to hang from:
  // its variable was filtered out of this view (another future, a hidden
  // scenario). Left in, the layout never places it and it lands at the origin
  // as an orphan card. It goes with its parent, and so does everything below.
  const present = new Set(nodes.map((n) => n.id));
  const orphanRoots = nodes.filter((n) => n.parentIds && n.parentIds.length > 0 && !present.has(n.parentIds[0]));
  if (orphanRoots.length) {
    const drop = new Set();
    const stack = [...orphanRoots];
    while (stack.length) {
      const n = stack.pop();
      if (drop.has(n.id)) continue;
      drop.add(n.id);
      for (const cid of n.children) if (nodeMap[cid]) stack.push(nodeMap[cid]);
    }
    console.log(`pathwaysToLayoutFormat: ${drop.size} node(s) hidden with their filtered-out parent (${orphanRoots.map(n => n.id.slice(0, 8)).join(', ')})`);
    nodes = nodes.filter((n) => !drop.has(n.id));
  }

  console.log('Nodes with children:', nodes.filter(n => n.children.length > 0).length);
  console.log('Root nodes (no children):', nodes.filter(n => n.children.length === 0).length);
  console.log('Tree structure:', nodes.map(n => ({ id: n.id, children: n.children })));

  return nodes;
}

/**
 * Transform layout output back to pathway format for state/database
 *
 * @param {Object} positions - Map of nodeId to {x, y}
 * @param {Array} pathways - Original pathways array
 * @returns {Array} Updated pathways with new positions
 */
export function layoutToPathwaysFormat(positions, pathways) {
  return pathways.map(pathway => {
    const position = positions[pathway.id];
    if (!position) return pathway;

    return {
      ...pathway,
      x: position.x,
      y: position.y
    };
  });
}

/**
 * Filter pathways by visible participants
 *
 * @param {Array} pathways - All pathways
 * @param {Set} visibleParticipants - Set of visible participant IDs
 * @returns {Array} Filtered pathways
 */
export function filterByVisibleParticipants(pathways, visibleParticipants) {
  if (!visibleParticipants || visibleParticipants.size === 0) {
    return pathways;
  }

  return pathways.filter(pathway => {
    // Always include start/target nodes
    if (pathway.type === 'start' || pathway.type === 'target') {
      return true;
    }

    // Filter by participant visibility
    return visibleParticipants.has(pathway.participantId);
  });
}

/**
 * Group pathways by participant for rendering
 *
 * @param {Array} pathways - Array of pathway objects
 * @returns {Array} Array of {participantId, participantName, pathways: []}
 */
export function groupPathwaysByParticipant(pathways) {
  const groups = {};

  pathways.forEach(pathway => {
    const participantId = pathway.participantId || 'unknown';

    if (!groups[participantId]) {
      groups[participantId] = {
        participantId,
        participantName: pathway.participantName || `Participant ${participantId.slice(0, 8)}`,
        pathways: []
      };
    }

    groups[participantId].pathways.push(pathway);
  });

  return Object.values(groups);
}

/**
 * Check if a pathway has saved position data
 *
 * @param {Object} pathway - Pathway object
 * @returns {boolean} True if pathway has saved position
 */
export function hasSavedPosition(pathway) {
  return (
    (pathway.x !== undefined && pathway.x !== null) ||
    (pathway.dragX !== undefined && pathway.dragX !== null)
  );
}

/**
 * Apply saved positions to layout output
 * If a node has a saved position, use it instead of calculated position
 *
 * @param {Object} positions - Map of nodeId to {x, y} from layout
 * @param {Array} pathways - Original pathways with saved positions
 * @returns {Object} Updated positions map
 */
export function applySavedPositions(positions, pathways) {
  const updated = { ...positions };

  pathways.forEach(pathway => {
    if (hasSavedPosition(pathway)) {
      updated[pathway.id] = {
        x: pathway.dragX || pathway.x,
        y: pathway.dragY || pathway.y
      };
    }
  });

  return updated;
}

/**
 * Calculate if layout needs recalculation
 * (e.g., when new nodes added, or reset requested)
 *
 * @param {Array} pathways - Current pathways
 * @param {Object} lastLayout - Last layout calculation metadata
 * @returns {boolean} True if layout needs recalculation
 */
export function needsLayoutRecalculation(pathways, lastLayout) {
  if (!lastLayout) return true;

  // Check if number of nodes changed
  if (pathways.length !== lastLayout.nodeCount) return true;

  // Check if any new nodes without positions
  const nodesWithoutPositions = pathways.filter(p => !hasSavedPosition(p));
  if (nodesWithoutPositions.length > 0) return true;

  return false;
}

export default {
  pathwaysToLayoutFormat,
  layoutToPathwaysFormat,
  filterByVisibleParticipants,
  groupPathwaysByParticipant,
  hasSavedPosition,
  applySavedPositions,
  needsLayoutRecalculation
};
